import { API_ENDPOINT, getAuthObj } from "../services/api";
import { APP_VERSION } from "../constants/app";
import { isDesktopEnvironment } from "./app";
import { getCookie, setCookie } from "./cookies";

/**
 * Internal product analytics transport. Events are queued and flushed in
 * batches to runtime's /analytics/collect; identity (uid/company) is attached
 * server-side from the usual auth object, the client never sends it explicitly.
 *
 * The body goes out as text/plain so the request stays a CORS "simple
 * request": no preflight, and navigator.sendBeacon can carry the last batch
 * when the tab closes.
 */

export type AnalyticsProps = Record<string, string | number | boolean>;

type QueuedEvent = {
    n: string;
    t: number;
    p: string;
    r?: string;
    x?: AnalyticsProps;
    utm?: Record<string, string>;
    /** visible seconds on the page, "pagetime" only */
    d?: number;
    /** engaged seconds within d (input within the last minute), "pagetime" only */
    e?: number;
};

const COLLECT_URL = API_ENDPOINT + "/analytics/collect";
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 20;
const SESSION_KEY = "ht.analytics.sid";
const REFERRER_KEY = "ht.analytics.refsent";
const UTM_KEYS = ["source", "medium", "campaign", "term", "content"];

/** Visible time is reported in chunks so long-lived dashboard tabs are never lost or capped. */
const PAGE_TIME_CHUNK_SECONDS = 300;
const PAGE_TIME_TICK_MS = 30000;
/** A user counts as engaged for this long after any mouse, keyboard, scroll or touch input. */
const ENGAGED_WINDOW_MS = 60000;

const queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let listenersInstalled = false;

/** The page currently on screen; visible time only, paused while the tab is hidden. */
let currentPage: { path: string; visibleSince: number | undefined; accumulatedMs: number; engagedMs: number } | undefined;
let pageTimeTicker: ReturnType<typeof setInterval> | undefined;
/** Open engagement window: starts at the first input, ends a minute after the latest one. */
let engagedSince: number | undefined;
let engagedUntil = 0;

export function sanitizeEventName(name: string): string {
    const cleaned = (name || "")
        .toLowerCase()
        .replace(/[^a-z0-9_:.-]+/g, "_")
        .replace(/^[^a-z0-9]+/, "")
        .slice(0, 80);
    return cleaned || "unknown";
}

export function trackEvent(name: string, props?: AnalyticsProps) {
    try {
        enqueue({
            n: sanitizeEventName(name),
            t: Date.now(),
            p: currentPath(),
            x: props && Object.keys(props).length > 0 ? props : undefined,
        });
    } catch {
        // analytics must never break the app
    }
}

export function trackPageView(path?: string) {
    try {
        leaveCurrentPage();
        const nextPath = path || currentPath();
        currentPage = { path: nextPath, visibleSince: isHidden() ? undefined : Date.now(), accumulatedMs: 0, engagedMs: 0 };
        engagedSince = undefined;
        ensurePageTimeTicker();
        const event: QueuedEvent = {
            n: "pageview",
            t: Date.now(),
            p: nextPath,
        };
        const referrer = firstReferrer();
        if (referrer) {
            event.r = referrer;
        }
        const utm = utmFromLocation();
        if (utm) {
            event.utm = utm;
        }
        const email = emailFromLocation();
        if (email) {
            event.x = { m: email };
        }
        enqueue(event);
    } catch {
        // analytics must never break the app
    }
}

/** Sends whatever is queued right away; `beacon` is for pagehide/visibility loss. */
export function flushAnalytics(beacon = false) {
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = undefined;
    }
    if (queue.length === 0) {
        return;
    }
    const events = queue.splice(0, queue.length);
    let body: string;
    try {
        body = JSON.stringify(payload(events));
    } catch {
        return;
    }
    try {
        if (beacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            if (navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: "text/plain" }))) {
                return;
            }
        }
        fetch(COLLECT_URL, {
            method: "POST",
            body,
            keepalive: true,
            headers: { "Content-Type": "text/plain" },
        }).catch(() => undefined);
    } catch {
        // offline, blocked, or shutting down: drop the batch
    }
}

function isHidden(): boolean {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function onUserInput() {
    if (!currentPage || currentPage.visibleSince === undefined) {
        return;
    }
    const now = Date.now();
    settleEngagement(now);
    if (engagedSince === undefined) {
        engagedSince = now;
    }
    engagedUntil = now + ENGAGED_WINDOW_MS;
}

/** Books the engagement window up to `now` (or its end) into the current page and closes it if it expired. */
function settleEngagement(now: number, close = false) {
    if (engagedSince === undefined) {
        return;
    }
    const end = Math.min(now, engagedUntil);
    if (currentPage && end > engagedSince) {
        currentPage.engagedMs += end - engagedSince;
    }
    if (close || now >= engagedUntil) {
        engagedSince = undefined;
    } else {
        engagedSince = now;
    }
}

function pauseCurrentPage() {
    if (currentPage && currentPage.visibleSince !== undefined) {
        const now = Date.now();
        settleEngagement(now, true);
        currentPage.accumulatedMs += now - currentPage.visibleSince;
        currentPage.visibleSince = undefined;
    }
}

function resumeCurrentPage() {
    if (currentPage && currentPage.visibleSince === undefined) {
        currentPage.visibleSince = Date.now();
    }
}

/** Reports the visible seconds accumulated so far as a "pagetime" event and starts a new chunk. */
function reportPageTime() {
    if (!currentPage) {
        return;
    }
    const wasVisible = currentPage.visibleSince !== undefined;
    const now = Date.now();
    const stillEngaged = wasVisible && engagedSince !== undefined && now < engagedUntil;
    pauseCurrentPage();
    const seconds = Math.round(currentPage.accumulatedMs / 1000);
    const engaged = Math.min(seconds, Math.round(currentPage.engagedMs / 1000));
    currentPage.accumulatedMs = 0;
    currentPage.engagedMs = 0;
    if (wasVisible) {
        resumeCurrentPage();
        if (stillEngaged) {
            engagedSince = now; // the window carries over into the next chunk
        }
    }
    if (seconds >= 1) {
        enqueue({ n: "pagetime", t: now, p: currentPage.path, d: seconds, e: engaged });
    }
}

function leaveCurrentPage() {
    if (!currentPage) {
        return;
    }
    pauseCurrentPage();
    reportPageTime();
    currentPage = undefined;
}

/** Every 30s: once a visible chunk reaches 5 minutes, report it. A crash loses at most one chunk. */
function ensurePageTimeTicker() {
    if (pageTimeTicker) {
        return;
    }
    pageTimeTicker = setInterval(() => {
        if (!currentPage || currentPage.visibleSince === undefined) {
            return;
        }
        const now = Date.now();
        settleEngagement(now);
        const visibleMs = currentPage.accumulatedMs + (now - currentPage.visibleSince);
        if (visibleMs >= PAGE_TIME_CHUNK_SECONDS * 1000) {
            reportPageTime();
        }
    }, PAGE_TIME_TICK_MS);
}

function enqueue(event: QueuedEvent) {
    installListeners();
    queue.push(event);
    if (queue.length >= MAX_QUEUE) {
        flushAnalytics();
        return;
    }
    if (!flushTimer) {
        flushTimer = setTimeout(() => flushAnalytics(), FLUSH_INTERVAL_MS);
    }
}

function payload(events: QueuedEvent[]) {
    return {
        app: "base",
        v: APP_VERSION,
        env: isDesktopEnvironment() ? "desktop" : "web",
        sid: sessionId(),
        // the website's visitor cookie; runtime stores it on accounts created at signup,
        // so events from before the account existed can be attributed to it later
        cid: clientId(),
        lang: typeof navigator !== "undefined" ? navigator.language : undefined,
        tz: timeZone(),
        sw: typeof window !== "undefined" && window.screen ? window.screen.width : undefined,
        now: Date.now(),
        auth: authObject(),
        events,
    };
}

function authObject() {
    try {
        const auth = getAuthObj() as { usr?: string } | undefined;
        return auth && auth.usr ? auth : undefined;
    } catch {
        return undefined;
    }
}

/**
 * The website's visitor cookie (same name, format and shared domain). Created
 * here too when missing, so signups that start directly on the dashboard get
 * one: it is sent with the signup call, stored on the new accounts, and lets
 * the anonymous signup steps be attributed to the company afterwards.
 */
export function clientId(): string | undefined {
    try {
        const existing = getCookie("ht_vid");
        if (existing) {
            return existing;
        }
        if (!window.location.hostname.endsWith("hellotracks.com")) {
            return undefined; // setCookie pins the shared domain; would not stick elsewhere
        }
        const created = "vid_" + Date.now() + "_" + Math.random().toString(36).substring(2, 15);
        setCookie("ht_vid", created, 730);
        return created;
    } catch {
        return undefined;
    }
}

function currentPath(): string {
    return typeof window !== "undefined" ? window.location.pathname : "";
}

function timeZone(): string | undefined {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return undefined;
    }
}

function sessionId(): string {
    try {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
            sessionStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    } catch {
        return "nosession";
    }
}

/** document.referrer is fixed for the whole SPA lifetime, so only report it once per session. */
function firstReferrer(): string | undefined {
    try {
        if (sessionStorage.getItem(REFERRER_KEY)) {
            return undefined;
        }
        sessionStorage.setItem(REFERRER_KEY, "1");
        const referrer = document.referrer;
        if (!referrer) {
            return undefined;
        }
        const host = new URL(referrer).host;
        return host && host !== window.location.host ? referrer : undefined;
    } catch {
        return undefined;
    }
}

function utmFromLocation(): Record<string, string> | undefined {
    try {
        const params = new URLSearchParams(window.location.search);
        const utm: Record<string, string> = {};
        for (const key of UTM_KEYS) {
            const value = params.get("utm_" + key);
            if (value) {
                utm[key] = value;
            }
        }
        return Object.keys(utm).length > 0 ? utm : undefined;
    } catch {
        return undefined;
    }
}

/** The onboarding emails mark their dashboard links with the email they're in (?m=welcome), so a visit from one counts. */
export function emailFromLocation(): string | undefined {
    try {
        const email = new URLSearchParams(window.location.search).get("m");
        return email && /^[a-z0-9_]{1,40}$/.test(email) ? email : undefined;
    } catch {
        return undefined;
    }
}

function installListeners() {
    if (listenersInstalled || typeof window === "undefined") {
        return;
    }
    listenersInstalled = true;
    let lastInput = 0;
    const onInput = () => {
        const now = Date.now();
        if (now - lastInput < 1000) {
            return; // throttle: mousemove fires constantly
        }
        lastInput = now;
        onUserInput();
    };
    for (const type of ["pointerdown", "pointermove", "keydown", "wheel", "scroll", "touchstart"]) {
        window.addEventListener(type, onInput, { passive: true, capture: true });
    }
    window.addEventListener("pagehide", () => {
        leaveCurrentPage();
        flushAnalytics(true);
    });
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            // report what was accumulated: a hidden tab may never come back
            reportPageTime();
            pauseCurrentPage();
            flushAnalytics(true);
        } else {
            resumeCurrentPage();
        }
    });
}
